import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Package, ClipboardList, TrendingUp, AlertTriangle, DollarSign, Layers, CheckCircle } from 'lucide-react';
import { useSalesTracking } from '../hooks/useSalesTracking';
import { usePageTitle } from '../hooks/usePageTitle';
import { trackProductPageView } from '../utils/analytics';
import { APP_NAME } from '../config/branding';

import IsometricInventoryManagement from '../components/IsometricInventoryManagement';

const InventoryManagement: React.FC = () => {
  usePageTitle('Veterinary Inventory Management - Track Consumables & Purchase Orders');

  useSalesTracking();

  useEffect(() => {
    trackProductPageView('inventory_management');
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="mb-4">
              <span className="inline-block px-3 py-1 bg-purple-100 text-purple-600 text-sm font-medium rounded-full">
                Inventory Management
              </span>
            </div>

            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Know exactly what you have, what you need
            </h1>

            <p className="text-xl text-gray-600 mb-8">
              Track every syringe, vaccine vial, and consumable in real-time. Automatically deduct inventory when tied to appointments and generate purchase orders before you run out.
            </p>

            <div className="flex gap-4 mb-8">
              <Link
                to="/pricing"
                className="bg-purple-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors"
              >
                See pricing
              </Link>
              <Link
                to="/contact"
                className="bg-gray-100 text-gray-900 px-6 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                Request demo →
              </Link>
            </div>
          </div>

          <div className="relative h-[400px] w-full flex items-center justify-center bg-transparent">
            <IsometricInventoryManagement />
          </div>
        </div>

        {/* Feature Highlights */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Layers className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">Consumables Tracking</h3>
              <p className="text-sm text-gray-600">Track vaccines, medications, syringes, and supplies with lot numbers and expiration dates.</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <ClipboardList className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">Purchase Orders</h3>
              <p className="text-sm text-gray-600">Generate POs automatically when stock runs low. Track orders from vendors to delivery.</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <TrendingUp className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">Appointment-Tied Usage</h3>
              <p className="text-sm text-gray-600">Inventory automatically deducts when items are used during appointments.</p>
            </div>
          </div>
        </div>
      </div>

      {/* How It Works Section */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">How It Works</h2>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-purple-600">1</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Add Your Inventory</h3>
              <p className="text-gray-600">Import your current stock or add items manually. Set reorder points and preferred vendors.</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-purple-600">2</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Use During Appointments</h3>
              <p className="text-gray-600">When you add items to an encounter, inventory is automatically updated in real-time.</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-purple-600">3</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Reorder Automatically</h3>
              <p className="text-gray-600">Get alerts when stock is low. Generate purchase orders with one click.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="py-16">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Why Practices Love It</h2>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-900">Never run out of critical supplies</h3>
                <p className="text-gray-600">Automatic low-stock alerts ensure you always have what you need.</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-900">Track expiration dates</h3>
                <p className="text-gray-600">Get notified before vaccines and medications expire.</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-900">Accurate cost tracking</h3>
                <p className="text-gray-600">Know exactly what each procedure costs in consumables.</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-900">Lot number traceability</h3>
                <p className="text-gray-600">Track which lot was used for which patient for recalls.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-16 bg-gray-900">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Stop losing money to expired inventory
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Join practices that have reduced waste and improved efficiency with smart inventory management.
          </p>
          <div className="flex gap-4 justify-center">
            <Link
              to="/pricing"
              className="bg-purple-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors"
            >
              See Plans & Pricing
            </Link>
            <Link
              to="/contact"
              className="bg-gray-800 text-white px-8 py-3 rounded-lg font-medium hover:bg-gray-700 transition-colors border border-gray-700"
            >
              Talk to sales
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InventoryManagement;

